import { AccountBookingCard } from '../../components/AccountBookingCard';
import { AccountSideMenu } from '../../composites/AccountSideMenu';
import {
  MainColumn,
  OrderList,
  PageGrid,
  PageTitle,
  PageWrapper,
  Section,
  SectionTitle,
} from './AccountBookingsTemplate.styles';
import type {
  AccountBookingsOrder,
  AccountBookingsTemplateLabels,
  AccountBookingsTemplateProps,
} from './AccountBookingsTemplate.types';

const orderLabelsFromTemplate = (labels: AccountBookingsTemplateLabels) => ({
  reference: labels.reference,
  date: labels.date,
  total: labels.total,
  view: labels.view,
  addToBooking: labels.addToBooking,
  imagePlaceholder: labels.imagePlaceholder,
  loading: labels.loading,
  statuses: labels.statuses,
});

const renderOrder = (order: AccountBookingsOrder, labels: AccountBookingsTemplateLabels) => {
  if (order.isLoading) {
    return (
      <AccountBookingCard
        key={order.id}
        variant="list-loading"
        labels={{ loading: labels.loading }}
      />
    );
  }
  return (
    <AccountBookingCard
      key={order.id}
      variant="list"
      images={order.images}
      reference={order.reference}
      status={order.status}
      dates={order.dates}
      total={order.total}
      labels={orderLabelsFromTemplate(labels)}
      onViewBooking={order.onViewBooking}
      onAddToBooking={order.onAddToBooking}
    />
  );
};

const renderLoadingCards = (count: number, labels: AccountBookingsTemplateLabels) =>
  Array.from({ length: count }, (_, index) => (
    <AccountBookingCard
      key={`loading-${index}`}
      variant="list-loading"
      labels={{ loading: labels.loading }}
    />
  ));

export function AccountBookingsTemplate({
  navItems,
  onSignOut,
  sideMenuTitle,
  sideMenuSignOutLabel,
  sideMenuNavigationLabel,
  activeOrders,
  previousOrders,
  loadingCount = 0,
  isLoading = false,
  onEmptyCtaClick,
  labels,
}: AccountBookingsTemplateProps) {
  const hasAny = activeOrders.length > 0 || previousOrders.length > 0;
  const showLoadingSection = loadingCount > 0 && !hasAny;
  const showEmptyState = !isLoading && !showLoadingSection && !hasAny;

  return (
    <PageWrapper data-testid="account-bookings-template">
      <PageGrid>
        <AccountSideMenu
          items={navItems}
          onSignOut={onSignOut}
          title={sideMenuTitle}
          signOutLabel={sideMenuSignOutLabel}
          navigationLabel={sideMenuNavigationLabel}
        />

        <MainColumn>
          <PageTitle>{labels.pageTitle}</PageTitle>

          {showLoadingSection && <OrderList>{renderLoadingCards(loadingCount, labels)}</OrderList>}

          {activeOrders.length > 0 && (
            <Section>
              <SectionTitle>{labels.activeSectionTitle}</SectionTitle>
              <OrderList>{activeOrders.map((order) => renderOrder(order, labels))}</OrderList>
            </Section>
          )}

          {previousOrders.length > 0 && (
            <Section>
              <SectionTitle>{labels.previousSectionTitle}</SectionTitle>
              <OrderList>{previousOrders.map((order) => renderOrder(order, labels))}</OrderList>
            </Section>
          )}

          {showEmptyState && (
            <AccountBookingCard
              variant="empty"
              message={labels.emptyMessage}
              ctaLabel={labels.emptyCta}
              onCtaClick={onEmptyCtaClick ?? (() => undefined)}
            />
          )}
        </MainColumn>
      </PageGrid>
    </PageWrapper>
  );
}
